<script lang="ts">
  import { initialsOf, tintOf } from '../avatar'

  /**
   * One person, as a coloured disc — BirdJournal.SupportSite's `Avatar.svelte`, reduced to the cases
   * this site has.
   *
   * Two of its three cases are gone on purpose. There is no `isAdmin`: staff wear the Bird Journal logo
   * on the support site because their posts appear among customers', and nothing here is ever seen by
   * anybody but its owner. And there is no `isAnonymous`: anonymity is about how somebody is published
   * to others, and this avatar is only ever shown to the person themselves.
   *
   * What is kept exactly is the tint, so the disc matches the one the same person has on the board.
   */
  let { name, size = 'md' }: { name: string | null; size?: 'sm' | 'md' | 'lg' } = $props()

  const initials = $derived(initialsOf(name ?? ''))
  const tint = $derived(tintOf(name ?? ''))
</script>

<span
  class="av {size}"
  style="--av-h:{tint.hue};--av-c:var(--bj-avatar-c-{tint.tone})"
  aria-hidden="true"
>
  {#if initials}
    {initials}
  {:else}
    <!-- No display name on the account. A glyph rather than a blank disc, and rather than falling back
         to a letter of the email address, which is not a name and would read as one. -->
    <svg viewBox="0 0 24 24" width="60%" height="60%" fill="currentColor">
      <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.4 0-8 2.2-8 5v1h16v-1c0-2.8-3.6-5-8-5Z" />
    </svg>
  {/if}
</span>

<style>
  /* Decorative: the name it belongs to is always beside it, so a screen reader reading both would say
     everything twice. */
  .av {
    border-radius: 50%;
    /* The sRGB line is the fallback for anything without OKLCH — same hue, near enough the same tint.
       The second declaration simply wins wherever oklch() parses. */
    background: hsl(var(--av-h) var(--bj-avatar-s-srgb) var(--bj-avatar-l-srgb));
    background: oklch(var(--bj-avatar-l) var(--av-c) var(--av-h));
    color: var(--bj-avatar-on);
    display: grid;
    place-items: center;
    font-weight: 700;
    flex: none;
    overflow: hidden;
    user-select: none;
  }
  /* Three fixed sizes rather than a free number: the app bar's is measured, and a size nobody has
     looked at is how a 34px target slips past the 48px floor. */
  .sm {
    width: 32px;
    height: 32px;
    font-size: 0.72rem;
  }
  .md {
    width: 38px;
    height: 38px;
    font-size: 0.88rem;
  }
  .lg {
    width: 44px;
    height: 44px;
    font-size: 0.9rem;
  }
</style>
